import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Button,
  MenuItem,
} from '@mui/material';
import api from '../api';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';

export default function CustomerForm() {
  const [formData, setFormData] = useState({
    name: '', phone: '', additionalPhone: '', idNumber: '',
    dateOfBirth: '', idIssueDate: '', marriageDate: '',
    address: '', city: '', status: '', payslipIncome: 0.0,
    username: '', password: '', healthFund: '', createdEmail: '',
    createdEmailPassword: '', newAddress: '', newCity: '',
    governmentAuthCode: '', spouseName: '', spousePhone: '',
    spouseIdNumber: '', spouseDateOfBirth: '', spouseIdIssueDate: '',
    spousePayslipIncome: 0.0, spouseUsername: '', spousePassword: '',
    bank: '', branch: '', accountNumber: '', numberOfChildren: 0,
    secondToLastChildBirthDate: '', lastChildBirthDate: '',
    recommenderName: '', doctorVisit1: '', doctorVisit2: '',
    passportIssueDate: '', passportNumber: '' ,
    spouseGovernmentAuthCode: '', customerStatus: '' 
  });

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      api.get(`/customers/${id}`).then(res => {
        setFormData(res.data);
      }).catch(err => {
        console.error("Failed to load customer", err);
      });
    }
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const dateFields = [
    'dateOfBirth', 'idIssueDate', 'marriageDate',
    'spouseDateOfBirth', 'spouseIdIssueDate',
    'secondToLastChildBirthDate', 'lastChildBirthDate',
    'passportIssueDate'
  ];

  const formatDates = (formData: Record<string, any>): Record<string, any> => {
    const formatted = { ...formData };
    for (const key of dateFields) {
      const value = formData[key];
      if (value) {
        try {
          const date = parseISO(value);
          if (isValid(date)) {
            formatted[key] = format(date, 'dd.MM.yyyy');
          }
        } catch {}
      }
    }
    return formatted;
  };

  const handleSubmit = async () => {
    const formattedFormData = formatDates(formData);
    if (id) {
      await api.put(`/customers/${id}`, formattedFormData);
    } else {
      await api.post('/customers', formattedFormData);
    }
    navigate('/customers');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const form = e.currentTarget as HTMLElement;
      const inputs = Array.from(form.querySelectorAll<HTMLElement>(
        'input, select, textarea'
      )).filter(el =>
        !el.hasAttribute('disabled') &&
        el.getAttribute('type') !== 'hidden' &&
        el.tabIndex !== -1
      );

      const index = inputs.indexOf(e.target as HTMLElement);
      if (index > -1 && index < inputs.length - 1) {
        inputs[index + 1].focus();
      }
    }
  };

  return (
    <Box
      component="form"
      dir="rtl"
      onSubmit={e => { e.preventDefault(); handleSubmit(); }}
      onKeyDown={handleKeyDown}
      sx={{
        p: 0,
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(7, 1fr)',
        },
        maxWidth: "100%",
        mx: 'auto',
      }}
    >
      <Paper sx={{ p: 0.5, gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" gutterBottom align="right" sx={{ fontWeight: 'bold' }}>
          הוספת לקוח חדש
        </Typography>
        <Button variant="contained" type="submit" sx={{ ml: 1, width: 220 }}>
          שמירה
        </Button>
      </Paper>

      <Typography
        variant="h6"
        sx={{ gridColumn: '1 / -1', fontWeight: 'bold', textAlign: 'left' }}
      >
        פרטים אישיים
      </Typography>

      {/* All TextFields below are right‑aligned */}
      <TextField
        label="שם"
        name="name"
        value={formData.name}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />
      <TextField
        label="סטטוס לקוח"
        name="customerStatus"
        value={formData.customerStatus}
        onChange={handleChange}
        select
        fullWidth
      >
        <MenuItem value="">לא ידוע</MenuItem>
        <MenuItem value="מועדף">מועדף</MenuItem>
        <MenuItem value="בטיפול">בטיפול</MenuItem>
        <MenuItem value="הוגש לוועדה">הוגש לוועדה</MenuItem>
        <MenuItem value="נקבע לו וועדה">נקבע לו וועדה</MenuItem>
        <MenuItem value="צריך מעקב">צריך מעקב</MenuItem>
      </TextField>

      <TextField
        label="טלפון"
        name="phone"
        value={formData.phone}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />
      <TextField
        label="טלפון נוסף"
        name="additionalPhone"
        value={formData.additionalPhone}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />
      <TextField
        label="ת.ז."
        name="idNumber"
        value={formData.idNumber}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />
      <TextField
        label="תאריך לידה"
        name="dateOfBirth"
        type="date"
        value={formData.dateOfBirth}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />
      <TextField
        label="תאריך הפנקת ת.ז."
        name="idIssueDate"
        type="date"
        value={formData.idIssueDate}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />
      <TextField
        label="תאריך נישואין"
        name="marriageDate"
        type="date"
        value={formData.marriageDate}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />
      <TextField
        label="כתובת"
        name="address"
        value={formData.address}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />
      <TextField
        label="עיר"
        name="city"
        value={formData.city}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />
      <TextField
        label="סטטוס"
        name="status"
        value={formData.status}
        onChange={(e) => setFormData(f => ({ ...f, status: e.target.value }))}
        select
        fullWidth
      >
        <MenuItem value={"Unknown"}>לא ידוע</MenuItem>
        <MenuItem value={"Single"}>רווק</MenuItem>
        <MenuItem value={"Married"}>נשוי</MenuItem>
        <MenuItem value={"Divorced"}>גרוש</MenuItem>
        <MenuItem value={"Widowed"}>אלמן</MenuItem>
      </TextField>
      <TextField
        label="סכום השתכרות בתלוש"
        name="payslipIncome"
        type="number"
        value={formData.payslipIncome}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />
      <TextField
        label="שם משתמש"
        name="username"
        value={formData.username}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />
      <TextField
        label="סיסמה"
        name="password"
        
        value={formData.password}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />
      
      <TextField
        label="קופת חולים"
        name="healthFund"
        value={formData.healthFund}
        onChange={handleChange}
        select
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      >
        <MenuItem value="Unknown">לא ידוע</MenuItem>
        <MenuItem value="Maccabi">מכבי</MenuItem>
        <MenuItem value="Leumit">לאומית</MenuItem>
        <MenuItem value="Clalit">כללית</MenuItem>
        <MenuItem value="Meuchedet">מאוחדת</MenuItem>
      </TextField>
      <TextField
        label="מייל שהוקם"
        name="createdEmail"
        value={formData.createdEmail}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />
      <TextField
        label="סיסמה למייל"
        name="createdEmailPassword"
        
        value={formData.createdEmailPassword}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />
      <TextField
        label="כתובת חדשה"
        name="newAddress"
        value={formData.newAddress}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />
      <TextField
        label="עיר חדשה"
        name="newCity"
        value={formData.newCity}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />
      <TextField
        label="קוד הזדהות ממשלתית"
        name="governmentAuthCode"
        value={formData.governmentAuthCode}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />

    <TextField
        label="מספר דרכון"
        name="passportNumber"
        value={formData.passportNumber}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />

    <TextField
        label="תאריך הנפקת דרכון"
        name="passportIssueDate"
        type="date"
        value={formData.passportIssueDate}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{  
          shrink: true,                
        }}
      />

        {/* Divider + Spouse section header */}
        {/* <Divider sx={{ gridColumn: '1 / -1', my: 3 }} /> */}
      <Typography
        variant="h6"
        sx={{ gridColumn: '1 / -1', fontWeight: 'bold', textAlign: 'left' }}
      >
        פרטי בת זוג
      </Typography>

      {/* Spouse fields */}
      <TextField
        label="שם"
        name="spouseName"
        value={formData.spouseName}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="טלפון"
        name="spousePhone"
        value={formData.spousePhone}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="ת.ז."
        name="spouseIdNumber"
        value={formData.spouseIdNumber}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="תאריך לידה"
        name="spouseDateOfBirth"
        type="date"
        value={formData.spouseDateOfBirth}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="תאריך הנפקת ת.ז."
        name="spouseIdIssueDate"
        type="date"
        value={formData.spouseIdIssueDate}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="סכום השתכרות בתלוש"
        name="spousePayslipIncome"
        type="number"
        value={formData.spousePayslipIncome}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="שם משתמש"
        name="spouseUsername"
        value={formData.spouseUsername}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="סיסמה"
        name="spousePassword"
        
        value={formData.spousePassword}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />

  <TextField
        label="קוד הזדהות ממשלתית"
        name="spouseGovernmentAuthCode"
        value={formData.spouseGovernmentAuthCode}
        onChange={handleChange}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />


<Typography
  variant="h6"
  sx={{ gridColumn: '1 / -1', fontWeight: 'bold', textAlign: 'left', mt: 0 }}
>
  פרטים כלליים
</Typography>

<TextField
  label="בנק"
  name="bank"
  value={formData.bank}
  onChange={handleChange}
  fullWidth
  InputLabelProps={{ shrink: true }}
/>
<TextField
  label="סניף"
  name="branch"
  value={formData.branch}
  onChange={handleChange}
  fullWidth
  InputLabelProps={{ shrink: true }}
/>
<TextField
  label="מס' ח-ן"
  name="accountNumber"
  value={formData.accountNumber}
  onChange={handleChange}
  fullWidth
  InputLabelProps={{ shrink: true }}
/>
<TextField
  label="מס' ילדים"
  name="numberOfChildren"
  type="number"
  value={formData.numberOfChildren}
  onChange={handleChange}
  fullWidth
  InputLabelProps={{ shrink: true }}
/>
<TextField
  label="תאריך לידה ילד אחד לפני אחרון"
  name="secondToLastChildBirthDate"
  type="date"
  value={formData.secondToLastChildBirthDate}
  onChange={handleChange}
  fullWidth
  InputLabelProps={{ shrink: true }}
/>
<TextField
  label="תאריך לידה ילד אחרון"
  name="lastChildBirthDate"
  type="date"
  value={formData.lastChildBirthDate}
  onChange={handleChange}
  fullWidth
  InputLabelProps={{ shrink: true }}
/>
<TextField
  label="שם ממליץ"
  name="recommenderName"
  value={formData.recommenderName}
  onChange={handleChange}
  fullWidth
  InputLabelProps={{ shrink: true }}
/>
<TextField
  label="ביקור רופא 1"
  name="doctorVisit1"
  value={formData.doctorVisit1}
  onChange={handleChange}
  fullWidth
  InputLabelProps={{ shrink: true }}
/>
<TextField
  label="ביקור רופא 2"
  name="doctorVisit2"
  value={formData.doctorVisit2}
  onChange={handleChange}
  fullWidth
  InputLabelProps={{ shrink: true }}
/>

    </Box>
  );
}
